import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { isEmpty } from "lodash";

import DataTable from "../../containers/DataTable";
import ConfirmDialog from "../../components/ConfirmDialog";
import GoverningBodyFilters from "../../containers/GoverningBodyFilters";
import GoverningBodyCardGrid from "../../containers/GoverningBodyCardGrid";
import {
  defaultParamsGoverningBodiesTable,
  GOVERNING_BODY_FIELDS_TITLE,
  GOVERNING_BODY_TEAM_TYPE_VALUES,
  MIN_SEARCH_KEY_LENGTH,
  TEAM_TYPE_OPTIONS,
  TIME_OUT_REQUEST,
} from "../../constants/data";
import { PAGES_TITLES, TITLE_BUTTONS, TITLES } from "../../constants/titles";
import { PATHS } from "../../constants/paths";
import { updateDataGoverningBodies } from "../../utils/getGoverningBodyDataForTable";
import {
  getGoverningBodiesAdminList,
  saveGoverningBodyAdmin,
  softDeleteGoverningBodyAdmin,
  getPointSchemas,
  getRuleGroups,
  getDdLists,
  searchGoverningBodyTeams,
  searchParentGoverningBodies,
} from "./sliceReducer";

const useStyles = makeStyles({
  page: {
    backgroundColor: "#f7f9fc",
    minHeight: "calc(100vh - 64px)",
    paddingTop: "24px",
    paddingBottom: "24px",
  },
  header: {
    marginBottom: "16px",
  },
  title: {
    fontSize: "34px",
    lineHeight: 1.1,
    fontWeight: 700,
    color: "#101828",
  },
  subtitle: {
    marginTop: "8px",
    fontSize: "16px",
    color: "#475467",
  },
  content: {
    marginTop: "16px",
    display: "flex",
    justifyContent: "center",
  },
  progress: {
    marginTop: "40px",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(220px, 1fr))",
    gap: "12px",
    minWidth: "560px",
    "@media (max-width: 760px)": {
      gridTemplateColumns: "1fr",
      minWidth: "280px",
    },
  },
  fullSpan: {
    gridColumn: "1 / -1",
  },
  formActions: {
    marginTop: "16px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
  },
  hiddenInput: {
    display: "none",
  },
  imageWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  imagePreview: {
    width: "100%",
    maxHeight: "220px",
    objectFit: "contain",
    border: "1px solid #d8e0ea",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
  },
  imagePlaceholder: {
    border: "1px dashed #d8e0ea",
    borderRadius: "8px",
    minHeight: "120px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#667085",
    fontSize: "13px",
  },
  imageActions: {
    display: "flex",
    gap: "8px",
  },
  helperText: {
    fontSize: "12px",
    color: "#667085",
  },
  ruleGroupsRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: "8px",
    alignItems: "center",
  },
});

const STORAGE_VIEW_MODE = "governingBodies:listView";
const MEET_TYPES = ["INVITATIONAL", "CHAMPIONSHIP", "DUAL"];
const GOVERNING_BODY_TEAM_TYPE_OPTIONS = TEAM_TYPE_OPTIONS.filter((item) =>
  GOVERNING_BODY_TEAM_TYPE_VALUES.includes(item.value),
);

const emptyFormTemplate = {
  status: "ACTIVE",
  ageCutoffMonth: 12,
  ageCutoffDay: 31,
  abbreviation: "",
  teamType: "",
  ddListId: "",
  parentId: "",
  parentOption: null,
  parentSearchQuery: "",
  teamId: "",
  teamOption: null,
  teamSearchQuery: "",
  imageUrl: null,
  imagePreviewUrl: "",
  imageDirty: false,
  translations: [{ locale: "en-US", name: "", shortName: "" }],
  ruleGroups: [],
  pointSchemaDefaults: [
    { meetType: "INVITATIONAL", pointSchemaId: "" },
    { meetType: "CHAMPIONSHIP", pointSchemaId: "" },
    { meetType: "DUAL", pointSchemaId: "" },
  ],
};

const getOptionValue = (item) =>
  item?.id ?? item?.ruleGroupId ?? item?.pointSchemaId ?? item?.value ?? item;

const getPointSchemaLabel = (item) => {
  const optionValue = getOptionValue(item);
  const description =
    toNull(item?.schemaDescription) ||
    toNull(item?.description) ||
    toNull(item?.name) ||
    toNull(item?.label) ||
    toNull(item?.title) ||
    toNull(item?.pointSchemaName);

  if (description) return `${description} (${optionValue})`;
  return String(optionValue);
};

const getRuleGroupLabel = (item) => {
  const optionValue = getOptionValue(item);
  const description =
    toNull(item?.description) ||
    toNull(item?.name) ||
    toNull(item?.label) ||
    toNull(item?.title) ||
    toNull(item?.ruleGroupName);

  if (description) return `${description} (${optionValue})`;
  return String(optionValue);
};

const parseIntOrNull = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const toNull = (value) => {
  if (value == null) return null;
  const str = String(value).trim();
  return str === "" ? null : str;
};

const normalizeTeamType = (value, allowedValues = []) => {
  const safeValue = String(value || "").trim().toUpperCase();
  if (!safeValue) return "";
  return allowedValues.includes(safeValue) ? safeValue : "";
};

const toBase64Only = (dataUrl) => {
  const raw = String(dataUrl || "");
  if (!raw) return null;
  const parts = raw.split(",");
  return parts.length > 1 ? parts.pop() : raw;
};

const mapTeamOption = (team) => {
  const teamId = parseIntOrNull(team?.id ?? team?.teamId);
  if (teamId == null) return null;
  const label = toNull(team?.name ?? team?.teamName) || `Team #${teamId}`;
  return { id: teamId, label };
};

const mapTeamOptions = (teams) => {
  const list = Array.isArray(teams) ? teams : [];
  const byId = new Map();

  list.forEach((team) => {
    const option = mapTeamOption(team);
    if (option) byId.set(option.id, option);
  });

  return Array.from(byId.values());
};

const mapParentGoverningBodyOption = (row) => {
  const id = parseIntOrNull(row?.id);
  if (id == null) return null;

  const abbreviation = toNull(row?.shortName) || toNull(row?.abbreviation);
  const name = toNull(row?.name);
  const label = abbreviation && name ? `${abbreviation} - ${name}` : name || abbreviation || `ID ${id}`;

  return { id, label };
};

const mapParentGoverningBodyOptions = (rows, currentId) => {
  const byId = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const option = mapParentGoverningBodyOption(row);
    if (!option) return;
    if (currentId != null && option.id === currentId) return;
    byId.set(option.id, option);
  });

  return Array.from(byId.values());
};

const normalizeSortField = (sort) => {
  if (sort === "Id") return "ID";
  return sort;
};

const buildPointSchemaDefaults = (pointSchemaDefaults) => {
  const safeValues = Array.isArray(pointSchemaDefaults) ? pointSchemaDefaults : [];
  const hasMeetTypeMapping = safeValues.some((item) => Boolean(item?.meetType));

  if (!hasMeetTypeMapping) {
    const schemaIds = safeValues
      .map((item) => parseIntOrNull(getOptionValue(item)))
      .filter((id) => id != null);

    if (schemaIds.length === 1) {
      return MEET_TYPES.map((meetType) => ({
        meetType,
        pointSchemaId: schemaIds[0],
      }));
    }

    return MEET_TYPES.map((meetType, index) => ({
      meetType,
      pointSchemaId: schemaIds[index] ?? null,
    }));
  }

  return MEET_TYPES.map((meetType) => {
    const found = safeValues.find((item) => item?.meetType === meetType);
    const resolvedPointSchemaId = parseIntOrNull(
      found?.pointSchemaId ?? getOptionValue(found) ?? "",
    );
    return {
      meetType,
      pointSchemaId: resolvedPointSchemaId,
    };
  });
};

const getDefaultFormValue = (row = {}) => {
  const translation =
    row?.translations?.find((item) => item?.locale === "en-US") ||
    row?.translations?.[0] ||
    {};
  const normalizedRuleGroups = (Array.isArray(row?.ruleGroups) ? row.ruleGroups : [])
    .map((item) => parseIntOrNull(getOptionValue(item)))
    .filter((id) => id != null);
  const normalizedPointSchemaDefaults = Array.isArray(row?.pointSchemaDefaults)
    ? row.pointSchemaDefaults
    : [];
  const normalizedPointSchemas =
    normalizedPointSchemaDefaults.length > 0
      ? normalizedPointSchemaDefaults
      : Array.isArray(row?.pointSchemas)
      ? row.pointSchemas
      : [];
  const normalizedParentId = parseIntOrNull(row?.parentId);
  const parentLabel =
    toNull(row?.parentShortName && row?.parentName
      ? `${row.parentShortName} - ${row.parentName}`
      : row?.parentName || row?.parentShortName) ||
    (normalizedParentId != null ? `ID ${normalizedParentId}` : "");
  const normalizedTeamId = parseIntOrNull(row?.teamId);
  const teamLabel = toNull(row?.teamName) || (normalizedTeamId != null ? `Team #${normalizedTeamId}` : "");

  return {
    ...emptyFormTemplate,
    ...row,
    status: row?.status || "ACTIVE",
    teamType: String(row?.teamType || "").trim().toUpperCase(),
    ageCutoffMonth: row?.ageCutoffMonth ?? 12,
    ageCutoffDay: row?.ageCutoffDay ?? 31,
    ddListId: row?.ddListId ?? "",
    parentId: row?.parentId ?? "",
    parentOption:
      normalizedParentId != null
        ? { id: normalizedParentId, label: parentLabel || `ID ${normalizedParentId}` }
        : null,
    parentSearchQuery: parentLabel || "",
    teamId: row?.teamId ?? "",
    teamOption:
      normalizedTeamId != null
        ? { id: normalizedTeamId, label: teamLabel || `Team #${normalizedTeamId}` }
        : null,
    teamSearchQuery: teamLabel || "",
    imageUrl: null,
    imagePreviewUrl: toNull(row?.imageMasterUrl) || toNull(row?.imageUrl) || "",
    imageDirty: false,
    translations: [
      {
        locale: "en-US",
        name: translation?.name ?? row?.name ?? "",
        shortName: translation?.shortName ?? row?.shortName ?? "",
      },
    ],
    ruleGroups: normalizedRuleGroups,
    pointSchemaDefaults: buildPointSchemaDefaults(normalizedPointSchemas),
  };
};

const normalizePayloadForSave = (values, selectedDto) => {
  const enUs = values?.translations?.find((item) => item?.locale === "en-US") || {};
  const teamId = parseIntOrNull(values.teamId);
  const parentId = parseIntOrNull(values.parentId);
  const ddListId = parseIntOrNull(values.ddListId);
  const imageUrl = toNull(values.imageUrl);
  const isEditMode = Boolean(selectedDto?.id);

  const payload = {
    ...(selectedDto?.id ? { id: selectedDto.id } : {}),
    abbreviation: String(values.abbreviation || "").toUpperCase(),
    status: values.status,
    teamType: normalizeTeamType(values.teamType, GOVERNING_BODY_TEAM_TYPE_VALUES),
    parentId,
    teamId,
    ddListId,
    ageCutoffMonth: parseIntOrNull(values.ageCutoffMonth),
    ageCutoffDay: parseIntOrNull(values.ageCutoffDay),
    translations: [
      {
        locale: "en-US",
        name: String(enUs.name || "").trim(),
        shortName: String(enUs.shortName || "").trim(),
      },
    ],
    ruleGroups: (values.ruleGroups || [])
      .map((value) => parseIntOrNull(value))
      .filter((value) => value != null),
    pointSchemaDefaults: buildPointSchemaDefaults(values.pointSchemaDefaults).filter(
      (item) => item.pointSchemaId != null,
    ),
  };

  if (!isEditMode || values?.imageDirty) {
    payload.imageUrl = imageUrl;
    payload.logoChanged = true;
  }

  return payload;
};

const validateForm = (values) => {
  const errors = [];
  const abbreviation = String(values?.abbreviation || "").trim().toUpperCase();
  const enUs = values?.translations?.find((item) => item?.locale === "en-US") || {};
  const month = parseIntOrNull(values?.ageCutoffMonth);
  const day = parseIntOrNull(values?.ageCutoffDay);

  if (!abbreviation || abbreviation.length < 2 || abbreviation.length > 10) {
    errors.push("Abbreviation must be 2-10 uppercase characters.");
  }
  if (!["ACTIVE", "INACTIVE", "DEPRECATED"].includes(values?.status)) {
    errors.push("Status is required.");
  }
  if (!GOVERNING_BODY_TEAM_TYPE_VALUES.includes(values?.teamType)) {
    errors.push("Team type is required.");
  }
  if (parseIntOrNull(values?.teamId) == null) {
    errors.push("Team selection is required.");
  }
  if (month == null || month < 1 || month > 12) {
    errors.push("Age cutoff month must be between 1 and 12.");
  }
  if (day == null || day < 1 || day > 31) {
    errors.push("Age cutoff day must be between 1 and 31.");
  }
  if (parseIntOrNull(values?.ddListId) == null) {
    errors.push("DD list is required.");
  }
  if (!String(enUs?.name || "").trim()) {
    errors.push("Translation name for en-US is required.");
  }
  if (!String(enUs?.shortName || "").trim()) {
    errors.push("Translation shortName for en-US is required.");
  }

  const locales = (values?.translations || []).map((item) => item?.locale).filter(Boolean);
  if (new Set(locales).size !== locales.length) {
    errors.push("Translations cannot contain duplicate locales.");
  }

  const pointSchemaDefaults = buildPointSchemaDefaults(values?.pointSchemaDefaults);
  if (pointSchemaDefaults.length !== 3) {
    errors.push("Point schema defaults must include INVITATIONAL, CHAMPIONSHIP, and DUAL.");
  }
  if (pointSchemaDefaults.some((item) => item?.pointSchemaId == null)) {
    errors.push("Each meet type must have a point schema selected.");
  }

  if (!Array.isArray(values?.ruleGroups) || values.ruleGroups.length < 1) {
    errors.push("At least one rule group must be selected.");
  }

  return errors;
};

const buildSoftDeletePayload = (row) => {
  const fromRow = getDefaultFormValue(row);
  const payload = normalizePayloadForSave(fromRow, row);
  return {
    ...payload,
    status: "DEPRECATED",
  };
};

const GoverningBodyFormShell = ({
  value,
  onChange,
  ddListOptions,
  pointSchemas,
  ruleGroups,
  parentOptions,
  parentOptionsLoading,
  onParentSearchInputChange,
  teamOptions,
  teamOptionsLoading,
  onTeamSearchInputChange,
  onUploadImage,
  onDeleteImage,
  onCancel,
  onSubmit,
  isSubmitting,
  onManageQualifyingScores,
}) => {
  const classes = useStyles();
  const [openRuleGroupsModal, setOpenRuleGroupsModal] = useState(false);
  const [draftRuleGroups, setDraftRuleGroups] = useState([]);

  const translation = value?.translations?.[0] || { locale: "en-US", name: "", shortName: "" };

  const setTranslation = (nextPatch) => {
    onChange({
      ...value,
      translations: [{ ...translation, ...nextPatch }],
    });
  };

  const setPointSchema = (meetType, pointSchemaId) => {
    const next = buildPointSchemaDefaults(value?.pointSchemaDefaults).map((item) =>
      item.meetType === meetType ? { ...item, pointSchemaId } : item,
    );

    onChange({
      ...value,
      pointSchemaDefaults: next,
    });
  };

  const hasImagePreview = Boolean(toNull(value?.imagePreviewUrl));
  const selectedRuleGroups = Array.isArray(value?.ruleGroups) ? value.ruleGroups : [];
  const ruleGroupsSummary = selectedRuleGroups
    .map((id) => {
      const found = (ruleGroups || []).find((item) => getOptionValue(item) === id);
      return found ? getRuleGroupLabel(found) : String(id);
    })
    .join(", ");

  const handleOpenRuleGroupsModal = () => {
    setDraftRuleGroups(selectedRuleGroups);
    setOpenRuleGroupsModal(true);
  };

  const handleCancelRuleGroupsModal = () => {
    setOpenRuleGroupsModal(false);
    setDraftRuleGroups(selectedRuleGroups);
  };

  const handleApplyRuleGroupsModal = () => {
    onChange({
      ...value,
      ruleGroups: draftRuleGroups,
    });
    setOpenRuleGroupsModal(false);
  };

  return (
    <Box>
      <Box className={classes.formGrid}>
        <TextField
          label="Abbreviation"
          variant="outlined"
          size="small"
          value={value?.abbreviation || ""}
          onChange={(event) =>
            onChange({
              ...value,
              abbreviation: String(event.target.value || "").toUpperCase(),
            })
          }
        />

        <FormControl variant="outlined" size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={value?.status || "ACTIVE"}
            onChange={(event) => onChange({ ...value, status: event.target.value })}
            label="Status"
          >
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            <MenuItem value="DEPRECATED">DEPRECATED</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small">
          <InputLabel>Team type</InputLabel>
          <Select
            value={value?.teamType || ""}
            onChange={(event) => onChange({ ...value, teamType: event.target.value })}
            label="Team type"
          >
            <MenuItem value="">
              <em>Select team type</em>
            </MenuItem>
            {value?.teamType &&
              !GOVERNING_BODY_TEAM_TYPE_VALUES.includes(value.teamType) && (
                <MenuItem value={value.teamType}>
                  {`${value.teamType} - Legacy (choose H/U/C/F/S before save)`}
                </MenuItem>
              )}
            {GOVERNING_BODY_TEAM_TYPE_OPTIONS.map((option) => (
              <MenuItem key={`governing-body-team-type-${option.value}`} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Name (en-US)"
          variant="outlined"
          size="small"
          value={translation?.name || ""}
          onChange={(event) => setTranslation({ name: event.target.value })}
        />

        <TextField
          label="Short name (en-US)"
          variant="outlined"
          size="small"
          value={translation?.shortName || ""}
          onChange={(event) => setTranslation({ shortName: event.target.value })}
        />

        <FormControl variant="outlined" size="small">
          <InputLabel>DD list</InputLabel>
          <Select
            value={value?.ddListId ?? ""}
            onChange={(event) => onChange({ ...value, ddListId: event.target.value })}
            label="DD list"
          >
            {(ddListOptions || []).map((item) => (
              <MenuItem key={`dd-list-${item.id}`} value={item.id}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          options={parentOptions || []}
          value={value?.parentOption || null}
          inputValue={value?.parentSearchQuery || ""}
          loading={parentOptionsLoading}
          onInputChange={(event, nextInput, reason) =>
            onParentSearchInputChange(nextInput || "", reason)
          }
          onChange={(event, nextOption) =>
            onChange({
              ...value,
              parentOption: nextOption || null,
              parentSearchQuery: nextOption?.label || "",
              parentId: nextOption?.id ?? "",
            })
          }
          getOptionLabel={(option) => option?.label || ""}
          getOptionSelected={(option, selected) => option?.id === selected?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Parent governing body"
              variant="outlined"
              size="small"
              placeholder="Optional"
            />
          )}
        />

        <Autocomplete
          options={teamOptions || []}
          value={value?.teamOption || null}
          inputValue={value?.teamSearchQuery || ""}
          loading={teamOptionsLoading}
          onInputChange={(event, nextInput, reason) =>
            onTeamSearchInputChange(nextInput || "", reason)
          }
          onChange={(event, nextOption) =>
            onChange({
              ...value,
              teamOption: nextOption || null,
              teamSearchQuery: nextOption?.label || "",
              teamId: nextOption?.id ?? "",
            })
          }
          getOptionLabel={(option) => option?.label || ""}
          getOptionSelected={(option, selected) => option?.id === selected?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Team"
              variant="outlined"
              size="small"
              placeholder="Type to search teams"
              helperText="Only teams with teamType=G are shown."
            />
          )}
        />

        <TextField
          label="Age cutoff month"
          variant="outlined"
          size="small"
          type="number"
          value={value?.ageCutoffMonth ?? ""}
          onChange={(event) => onChange({ ...value, ageCutoffMonth: event.target.value })}
        />

        <TextField
          label="Age cutoff day"
          variant="outlined"
          size="small"
          type="number"
          value={value?.ageCutoffDay ?? ""}
          onChange={(event) => onChange({ ...value, ageCutoffDay: event.target.value })}
        />

        <Box className={classes.fullSpan}>
          <Box className={classes.ruleGroupsRow}>
            <TextField
              label="Rule groups"
              variant="outlined"
              size="small"
              value={ruleGroupsSummary}
              placeholder="No rule groups selected"
              InputProps={{ readOnly: true }}
            />
            <Button variant="outlined" onClick={handleOpenRuleGroupsModal}>
              Select
            </Button>
          </Box>
        </Box>

        {MEET_TYPES.map((meetType) => {
          const current = buildPointSchemaDefaults(value?.pointSchemaDefaults).find(
            (item) => item.meetType === meetType,
          );
          return (
            <FormControl key={meetType} variant="outlined" size="small">
              <InputLabel>{`${meetType} schema`}</InputLabel>
              <Select
                value={current?.pointSchemaId ?? ""}
                onChange={(event) => setPointSchema(meetType, event.target.value)}
                label={`${meetType} schema`}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {(pointSchemas || []).map((item) => {
                  const optionValue = getOptionValue(item);
                  return (
                    <MenuItem key={`${meetType}-schema-${optionValue}`} value={optionValue}>
                      {getPointSchemaLabel(item)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          );
        })}

        <Box className={classes.fullSpan}>
          <Typography variant="subtitle2">Logo image</Typography>
          <Box className={classes.imageWrap}>
            {hasImagePreview ? (
              <img
                src={value?.imagePreviewUrl}
                alt="governing body logo preview"
                className={classes.imagePreview}
              />
            ) : (
              <Box className={classes.imagePlaceholder}>No image selected</Box>
            )}

            <Typography className={classes.helperText}>
              Upload sends base64 in `imageUrl` to the backend.
            </Typography>

            <Box className={classes.imageActions}>
              <input
                className={classes.hiddenInput}
                id="governing-body-image-upload"
                type="file"
                accept="image/*"
                onChange={onUploadImage}
              />
              <label htmlFor="governing-body-image-upload" style={{ width: "100%" }}>
                <Button fullWidth component="span" variant="contained" color="primary">
                  Upload image
                </Button>
              </label>
              <Button
                fullWidth
                variant="outlined"
                onClick={onDeleteImage}
                disabled={!hasImagePreview}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box className={classes.formActions}>
        {onManageQualifyingScores && (
          <Button
            variant="text"
            color="primary"
            onClick={onManageQualifyingScores}
            style={{ marginRight: "auto", textTransform: "none" }}
          >
            → Manage qualifying scores
          </Button>
        )}
        <Button onClick={onCancel}>{TITLE_BUTTONS.CANCEL}</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {TITLE_BUTTONS.FINISH}
        </Button>
      </Box>

      {openRuleGroupsModal && (
        <ConfirmDialog
          open
          handleClose={handleCancelRuleGroupsModal}
          title="Select rule groups"
        >
          <Box style={{ minWidth: 520, maxWidth: "85vw" }}>
            <Autocomplete
              multiple
              options={ruleGroups || []}
              value={(ruleGroups || []).filter((item) =>
                draftRuleGroups.includes(getOptionValue(item)),
              )}
              onChange={(event, selectedOptions) =>
                setDraftRuleGroups(
                  (selectedOptions || []).map((item) => getOptionValue(item)),
                )
              }
              disableCloseOnSelect
              getOptionLabel={(option) => getRuleGroupLabel(option)}
              getOptionSelected={(option, selected) =>
                getOptionValue(option) === getOptionValue(selected)
              }
              renderOption={(option, { selected }) => (
                <React.Fragment>
                  <Checkbox checked={selected} color="primary" style={{ marginRight: 8 }} />
                  {getRuleGroupLabel(option)}
                </React.Fragment>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  label="Rule groups"
                  placeholder="Type to filter"
                />
              )}
            />

            <Box className={classes.formActions}>
              <Button onClick={handleCancelRuleGroupsModal}>{TITLE_BUTTONS.CANCEL}</Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleApplyRuleGroupsModal}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </ConfirmDialog>
      )}
    </Box>
  );
};

const GoverningBodies = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, fetchDataGoverningBodies, pointSchemas, ruleGroups, ddLists } = useSelector(
    (state) => state.governingBodies,
  );

  const [dtos, setDtos] = useState([]);
  const [pagination, setPagination] = useState({
    ...defaultParamsGoverningBodiesTable,
    total: 0,
  });
  const [viewMode, setViewMode] = useState(
    localStorage.getItem(STORAGE_VIEW_MODE) || "card",
  );
  const [filters, setFilters] = useState({
    status: defaultParamsGoverningBodiesTable.status,
    teamType: defaultParamsGoverningBodiesTable.teamType,
    query: defaultParamsGoverningBodiesTable.query,
    locale: defaultParamsGoverningBodiesTable.locale,
    ddListId: null,
  });
  const [selectedDto, setSelectedDto] = useState({});
  const [open, setOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [formValue, setFormValue] = useState(emptyFormTemplate);
  const [isSaving, setIsSaving] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);
  const [teamOptionsLoading, setTeamOptionsLoading] = useState(false);
  const [parentOptions, setParentOptions] = useState([]);
  const [parentOptionsLoading, setParentOptionsLoading] = useState(false);
  const teamSearchTimeoutRef = useRef(null);
  const teamSearchRequestIdRef = useRef(0);
  const parentSearchTimeoutRef = useRef(null);
  const parentSearchRequestIdRef = useRef(0);

  const isEditMode = !isEmpty(selectedDto);
  const ddListOptions = useMemo(() => {
    const byId = new Map();
    (ddLists || []).forEach((item) => {
      const id = parseIntOrNull(item?.id);
      if (id == null) return;
      const label =
        toNull(item?.abbreviation) ||
        toNull(item?.ddListAbbreviation) ||
        toNull(item?.name) ||
        `DD List ${id}`;
      byId.set(id, { id, label });
    });
    (dtos || []).forEach((row) => {
      const id = parseIntOrNull(row?.ddListId);
      if (id == null) return;
      if (byId.has(id)) return;
      const label =
        toNull(row?.ddListAbbreviation) ||
        toNull(row?.ddListDescription) ||
        `DD List ${id}`;
      byId.set(id, { id, label });
    });
    const selectedId = parseIntOrNull(selectedDto?.ddListId);
    if (selectedId != null && !byId.has(selectedId)) {
      const selectedLabel =
        toNull(selectedDto?.ddListDescription) || `DD List ${selectedId}`;
      byId.set(selectedId, { id: selectedId, label: selectedLabel });
    }
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [ddLists, dtos, selectedDto?.ddListAbbreviation, selectedDto?.ddListDescription, selectedDto?.ddListId]);

  const runTeamSearch = async (rawQuery, withLoading = true) => {
    const query = String(rawQuery || "").trim();
    // An empty query loads the default list of governing-body teams.
    const queryForApi = query;
    const requestId = teamSearchRequestIdRef.current + 1;
    teamSearchRequestIdRef.current = requestId;

    if (withLoading) {
      setTeamOptionsLoading(true);
    }

    try {
      const payload = await dispatch(
        searchGoverningBodyTeams({
          query: queryForApi,
          page: 0,
          limit: 20,
        }),
      ).unwrap();

      if (requestId !== teamSearchRequestIdRef.current) return;

      const nextOptions = mapTeamOptions(payload);
      setTeamOptions(nextOptions);
    } catch (_) {
      if (requestId !== teamSearchRequestIdRef.current) return;
      setTeamOptions([]);
    } finally {
      if (requestId === teamSearchRequestIdRef.current) {
        setTeamOptionsLoading(false);
      }
    }
  };

  const handleTeamSearchInputChange = (nextInput, reason) => {
    const query = String(nextInput || "");

    setFormValue((prev) => ({
      ...prev,
      teamSearchQuery: query,
      ...(reason === "clear" ? { teamOption: null, teamId: "" } : {}),
    }));

    if (reason !== "input" && reason !== "clear") {
      return;
    }

    clearTimeout(teamSearchTimeoutRef.current);

    const trimmed = query.trim();
    if (trimmed.length > 0 && trimmed.length < MIN_SEARCH_KEY_LENGTH) {
      setTeamOptions([]);
      return;
    }

    teamSearchTimeoutRef.current = setTimeout(() => {
      runTeamSearch(trimmed, false);
    }, TIME_OUT_REQUEST);
  };

  const runParentSearch = async (
    rawQuery,
    withLoading = true,
    currentEntityIdOverride = null,
    pinnedParentOption = null,
  ) => {
    const query = String(rawQuery || "").trim();
    const queryForApi = query;
    const requestId = parentSearchRequestIdRef.current + 1;
    parentSearchRequestIdRef.current = requestId;

    if (withLoading) {
      setParentOptionsLoading(true);
    }

    try {
      const payload = await dispatch(
        searchParentGoverningBodies({
          query: queryForApi,
          page: 0,
          limit: 100,
          locale: filters?.locale || "en-US",
        }),
      ).unwrap();

      if (requestId !== parentSearchRequestIdRef.current) return;

      const currentId =
        currentEntityIdOverride != null
          ? parseIntOrNull(currentEntityIdOverride)
          : parseIntOrNull(selectedDto?.id);
      const currentParentOption = pinnedParentOption || formValue?.parentOption || null;
      const nextOptions = mapParentGoverningBodyOptions(payload, currentId);

      if (currentParentOption && !nextOptions.some((item) => item.id === currentParentOption.id)) {
        nextOptions.unshift(currentParentOption);
      }
      setParentOptions(nextOptions);
    } catch (_) {
      if (requestId !== parentSearchRequestIdRef.current) return;
      setParentOptions([]);
    } finally {
      if (requestId === parentSearchRequestIdRef.current) {
        setParentOptionsLoading(false);
      }
    }
  };

  const handleParentSearchInputChange = (nextInput, reason) => {
    const query = String(nextInput || "");

    if (reason === "clear") {
      setFormValue((prev) => ({
        ...prev,
        parentSearchQuery: "",
        parentOption: null,
        parentId: "",
      }));
      setParentOptions([]);
      return;
    }

    if (reason !== "input") {
      return;
    }

    setFormValue((prev) => ({
      ...prev,
      parentSearchQuery: query,
    }));

    clearTimeout(parentSearchTimeoutRef.current);

    const trimmed = query.trim();
    if (trimmed.length > 0 && trimmed.length < MIN_SEARCH_KEY_LENGTH) {
      setParentOptions([]);
      return;
    }

    parentSearchTimeoutRef.current = setTimeout(() => {
      runParentSearch(trimmed, false);
    }, TIME_OUT_REQUEST);
  };

  const handleUploadImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error("Image too large. Keep it under 5MB.");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const base64 = toBase64Only(dataUrl);

      if (!base64) {
        toast.error("Failed to process the selected image.");
        return;
      }

      setFormValue((prev) => ({
        ...prev,
        imageUrl: base64,
        imagePreviewUrl: dataUrl,
        imageDirty: true,
      }));
    };
    reader.onerror = () => {
      toast.error("Failed to read image file.");
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleDeleteImage = () => {
    setFormValue((prev) => ({
      ...prev,
      imageUrl: null,
      imagePreviewUrl: "",
      imageDirty: true,
    }));
  };

  const reloadList = (nextPagination = pagination, nextFilters = filters) => {
    return dispatch(
      getGoverningBodiesAdminList({
        pagination: {
          page: nextPagination.page,
          limit: nextPagination.limit,
          sort: nextPagination.sort,
          isAscending: nextPagination.isAscending,
        },
        filters: {
          status: nextFilters.status,
          teamType: nextFilters.teamType,
          query: nextFilters.query,
          locale: nextFilters.locale,
        },
      }),
    );
  };

  const handleChangePage = (payload) => {
    const nextPagination = {
      ...pagination,
      ...payload,
      sort: normalizeSortField(payload?.sort || pagination.sort),
    };
    setPagination(nextPagination);
    reloadList(nextPagination, filters);
  };

  const handleFiltersChange = (patch) => {
    const nextFilters = { ...filters, ...patch };
    const nextPagination = { ...pagination, page: 0 };

    setFilters(nextFilters);
    setPagination(nextPagination);
    reloadList(nextPagination, nextFilters);
  };

  const handleSortChange = (patch) => {
    const nextPagination = {
      ...pagination,
      ...patch,
      sort: normalizeSortField(patch?.sort || pagination.sort),
      page: 0,
    };

    setPagination(nextPagination);
    reloadList(nextPagination, filters);
  };

  const handleViewModeChange = (value) => {
    setViewMode(value);
    localStorage.setItem(STORAGE_VIEW_MODE, value);
  };

  const handleOpenCreate = () => {
    const nextForm = getDefaultFormValue({});
    setSelectedDto({});
    setFormValue(nextForm);
    setOpen(true);
    runTeamSearch("");
    runParentSearch("", true, null, nextForm.parentOption);
  };

  const handleOpenEdit = (row) => {
    const nextForm = getDefaultFormValue(row);
    setSelectedDto(row || {});
    setFormValue(nextForm);
    setOpen(true);
    runTeamSearch("");
    runParentSearch("", true, parseIntOrNull(row?.id), nextForm.parentOption);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedDto({});
    setFormValue(emptyFormTemplate);
    setTeamOptions([]);
    setTeamOptionsLoading(false);
    setParentOptions([]);
    setParentOptionsLoading(false);
    teamSearchRequestIdRef.current += 1;
    parentSearchRequestIdRef.current += 1;
    clearTimeout(teamSearchTimeoutRef.current);
    clearTimeout(parentSearchTimeoutRef.current);
  };

  const handleOpenDeleteModal = (row) => {
    setSelectedDto(row || {});
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedDto({});
  };

  const handleSave = async () => {
    const errors = validateForm(formValue);
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }

    try {
      setIsSaving(true);
      const payload = normalizePayloadForSave(formValue, selectedDto);
      // Hard guarantee: persist ddListId from the current form selection.
      payload.ddListId = parseIntOrNull(formValue?.ddListId);
      if (payload.ddListId == null) {
        toast.error("DD list is required.");
        return;
      }
      await dispatch(saveGoverningBodyAdmin({ data: payload })).unwrap();
      await reloadList();
      handleCloseModal();
    } catch (error) {
      toast.error(error?.message || "Failed to save governing body");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsSaving(true);
      const payload = buildSoftDeletePayload(selectedDto);
      await dispatch(softDeleteGoverningBodyAdmin({ data: payload })).unwrap();
      await reloadList();
      handleCloseDeleteModal();
    } catch (error) {
      toast.error(error?.message || "Failed to delete governing body");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      clearTimeout(teamSearchTimeoutRef.current);
      clearTimeout(parentSearchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    reloadList(
      {
        ...defaultParamsGoverningBodiesTable,
        total: 0,
      },
      filters,
    );
    dispatch(getPointSchemas());
    dispatch(getRuleGroups());
    dispatch(getDdLists());
    // search lookup is optional for phase 1 and should not block list screen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (!fetchDataGoverningBodies) return;

    const rows = Array.isArray(fetchDataGoverningBodies.rows)
      ? fetchDataGoverningBodies.rows
      : [];
    setDtos(rows);

    setPagination((prev) => ({
      ...prev,
      page: fetchDataGoverningBodies.currentPage ?? prev.page,
      total: fetchDataGoverningBodies.total ?? rows.length,
    }));
  }, [fetchDataGoverningBodies]);

  const tableActions = useMemo(
    () => [
      {
        key: "edit",
        title: "Edit",
        getLabel: () => TITLE_BUTTONS.EDIT,
        onClick: (row) => handleOpenEdit(row),
      },
      {
        key: "delete",
        title: "Delete",
        getLabel: () => TITLE_BUTTONS.DELETE,
        onClick: (row) => handleOpenDeleteModal(row),
      },
    ],
    [],
  );

  // The DD-list filter is applied client-side against the rows returned for the
  // current page — the backend list endpoint doesn't accept a ddListId param yet.
  // If the dataset grows, switch to server-side filtering on the backend instead
  // of bumping the page size here.
  const visibleDtos = useMemo(() => {
    if (filters?.ddListId == null) return dtos;
    const target = Number(filters.ddListId);
    return (dtos || []).filter((row) => Number(row?.ddListId) === target);
  }, [dtos, filters?.ddListId]);

  return (
    <Box className={classes.page}>
      <Container data-container="container">
        <Box className={classes.header}>
          <Typography className={classes.title}>{PAGES_TITLES.GOVERNING_BODIES}</Typography>
          <Typography className={classes.subtitle}>
            Manage governing bodies, status, images, and scoring configuration.
          </Typography>
        </Box>

        <GoverningBodyFilters
          filters={filters}
          pagination={pagination}
          viewMode={viewMode}
          ddLists={ddLists}
          onFiltersChange={handleFiltersChange}
          onSortChange={handleSortChange}
          onViewModeChange={handleViewModeChange}
          onCreate={handleOpenCreate}
        />

        <Box className={classes.content}>
          {loading ? (
            <CircularProgress className={classes.progress} />
          ) : viewMode === "table" ? (
            <DataTable
              updateItem={handleOpenEdit}
              fetchData={visibleDtos}
              pagination={pagination}
              removeRow={() => {}}
              onChangePage={handleChangePage}
              updateData={updateDataGoverningBodies}
              fieldsRow={GOVERNING_BODY_FIELDS_TITLE}
              actions={tableActions}
            />
          ) : (
            <GoverningBodyCardGrid
              rows={visibleDtos}
              pagination={pagination}
              onChangePage={handleChangePage}
              onEdit={handleOpenEdit}
              onDelete={handleOpenDeleteModal}
            />
          )}
        </Box>
      </Container>

      {open && (
        <ConfirmDialog
          open
          handleClose={handleCloseModal}
          title={isEditMode ? TITLES.EDIT_GOVERNING_BODY : TITLES.CREATE_GOVERNING_BODY}
        >
          <GoverningBodyFormShell
            value={formValue}
            onChange={setFormValue}
            ddListOptions={ddListOptions}
            pointSchemas={pointSchemas}
            ruleGroups={ruleGroups}
            parentOptions={parentOptions}
            parentOptionsLoading={parentOptionsLoading}
            onParentSearchInputChange={handleParentSearchInputChange}
            teamOptions={teamOptions}
            teamOptionsLoading={teamOptionsLoading}
            onTeamSearchInputChange={handleTeamSearchInputChange}
            onUploadImage={handleUploadImage}
            onDeleteImage={handleDeleteImage}
            onCancel={handleCloseModal}
            onSubmit={handleSave}
            isSubmitting={isSaving}
            onManageQualifyingScores={
              isEditMode && selectedDto?.id
                ? () => {
                    handleCloseModal();
                    navigate(
                      `${PATHS.QUALIFYING_SCORES}?governingBodyId=${selectedDto.id}`,
                    );
                  }
                : undefined
            }
          />
        </ConfirmDialog>
      )}

      {openDeleteModal && (
        <ConfirmDialog open handleClose={handleCloseDeleteModal} title={TITLES.DELETE_MODAL}>
          <Typography>
            This action will set status to <b>DEPRECATED</b>.
          </Typography>
          <Box className={classes.formActions}>
            <Button onClick={handleCloseDeleteModal}>{TITLE_BUTTONS.CANCEL}</Button>
            <Button
              color="secondary"
              variant="contained"
              onClick={handleDelete}
              disabled={isSaving}
            >
              {TITLE_BUTTONS.DELETE}
            </Button>
          </Box>
        </ConfirmDialog>
      )}
    </Box>
  );
};

export default GoverningBodies;
